
import { supabase } from '@/integrations/supabase/client';

interface ErrorLogEntry {
  timestamp: string;
  message: string;
  context: string;
  stack?: string;
}

/**
 * Log an error to the server
 * @param error The error object or error message
 * @param context Optional context where the error occurred
 */
export const logError = async (error: Error | string, context?: string) => {
  try {
    // Get the current user data
    const { data } = await supabase.auth.getUser();
    
    const errorMessage = typeof error === 'string' ? error : error.message;
    const errorStack = typeof error === 'string' ? undefined : error.stack;
    const errorContext = context || 'unknown';
    
    console.error(`Error in ${errorContext}:`, errorMessage);
    if (errorStack) console.error(errorStack);
    
    // Log to localStorage for debugging
    try {
      const errorLogs: ErrorLogEntry[] = JSON.parse(localStorage.getItem('error_logs') || '[]');
      errorLogs.unshift({
        timestamp: new Date().toISOString(),
        message: errorMessage,
        context: errorContext,
        stack: errorStack?.slice(0, 500)
      });
      // Keep only the last 20 errors
      localStorage.setItem('error_logs', JSON.stringify(errorLogs.slice(0, 20)));
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
    
    // Call the error-logger edge function
    await supabase.functions.invoke('error-logger', {
      method: 'POST',
      body: {
        message: errorMessage,
        stack: errorStack,
        path: context || window.location.pathname,
        userId: data?.user?.id,
        timestamp: new Date().toISOString()
      }
    });

    // Dispatch error event for components that need to handle errors
    window.dispatchEvent(new CustomEvent('app-error', { 
      detail: { 
        message: errorMessage, 
        context: errorContext 
      } 
    }));
  } catch (loggingError) {
    // If we can't log the error, at least console it
    console.error('Failed to log error:', loggingError);
    console.error('Original error:', error);
  }
};

/**
 * Set up global error handling to catch and log unhandled errors
 */
export const setupGlobalErrorLogging = () => {
  if (typeof window !== 'undefined') {
    // Handle uncaught errors
    window.addEventListener('error', (event) => {
      logError(event.error || event.message, window.location.pathname);
    });
    
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason instanceof Error 
        ? event.reason 
        : new Error(String(event.reason));
      
      logError(error, window.location.pathname);
    });
  }
};

/**
 * Get error logs from localStorage for debugging
 */
export const getErrorLogs = (): ErrorLogEntry[] => {
  try {
    return JSON.parse(localStorage.getItem('error_logs') || '[]');
  } catch (e) {
    console.error('Error retrieving error logs:', e);
    return [];
  }
};

/**
 * Clear error logs from localStorage
 */
export const clearErrorLogs = (): void => {
  try {
    localStorage.setItem('error_logs', '[]');
  } catch (e) {
    console.error('Error clearing error logs:', e);
  }
};
